import { Controller } from "@hotwired/stimulus"

// Controller identifier: windows-event-log-toggle
// Handles:
// - Radio button toggles for Windows Event Log collection settings
// - Confirmation modal before applying changes
// - Works for both account-level and organization-level settings
// - Turbo form submission and response handling
export default class extends Controller {
  static targets = ["radio", "form"]
  static values = {
    modalId: String,
    level: String,
    accountItemCount: String,
    organizationItemCount: String,
  }

  initialize() {
    this.originalValue = null
    this.pendingValue = null
    this.showingModal = false
  }

  connect() {
    this.storeOriginalValue()
  }

  storeOriginalValue() {
    const checkedRadio = this.radioTargets.find(radio => radio.checked)
    if (checkedRadio) {
      this.originalValue = checkedRadio.value
    }
  }

  onChange(event) {
    if (this.showingModal) {
      event.preventDefault()
      return
    }

    const newValue = event.target.value
    const oldValue = this.originalValue

    if (newValue === oldValue) {
      return
    }


    this.pendingValue = newValue
    this.showConfirmationModal()
  }

  showConfirmationModal() {
    this.showingModal = true

    const counts = this.calculateAffectedCounts()

    const modalController = this.getModalController()
    if (modalController) {
      modalController.show(counts.enabling, counts.disabling, { source: "windows-event-log-toggle" })
    }
  }

  calculateAffectedCounts() {
    const isEnabling = this.pendingValue === "1"

    if (this.levelValue === "account") {
      return {
        enabling: isEnabling ? this.getAccountItemCount() : 0,
        disabling: isEnabling ? 0 : this.getAccountItemCount()
      }
    } else if (this.levelValue === "organization") {
      return {
        enabling: isEnabling ? this.getOrganizationItemCount() : 0,
        disabling: isEnabling ? 0 : this.getOrganizationItemCount()
      }
    }

    return { enabling: 0, disabling: 0 }
  }

  getAccountItemCount() {
    return this.accountItemCountValue
  }

  getOrganizationItemCount() {
    return this.organizationItemCountValue
  }

  getModalController() {
    const modalId = this.modalIdValue
    if (modalId) {
      const modalElement = document.getElementById(modalId)
      if (modalElement) {
        return this.application.getControllerForElementAndIdentifier(modalElement, "confirmation-modal")
      }
    }
    // Fallback: look for a modal within this controller's element
    const modalElement = this.element.querySelector(".modal")
    if (modalElement) {
      return this.application.getControllerForElementAndIdentifier(modalElement, "confirmation-modal")
    }

    return null
  }


  submitForm() {
    this.showingModal = false

    const targetRadio = this.radioTargets.find(radio => radio.value === this.pendingValue)
    if (targetRadio) {
      targetRadio.checked = true
    }

    const form = this.hasFormTarget ? this.formTarget : this.element.querySelector("form")

    if (form && typeof form.requestSubmit === "function") {
      form.requestSubmit()
    }
  }

  resetForm() {
    this.showingModal = false
    this.revertRadioSelection()
    this.pendingValue = null
  }

  revertRadioSelection() {
    if (this.originalValue !== null) {
      const originalRadio = this.radioTargets.find(radio => radio.value === this.originalValue)
      if (originalRadio) {
        originalRadio.checked = true
      }
    }
  }

  afterSubmit(event) {
    const { success } = event.detail

    if (success) {
      this.storeOriginalValue()
      this.pendingValue = null
    } else {
      this.revertRadioSelection()
      this.pendingValue = null
    }

    this.showingModal = false
  }
}
